import { mapViewBounds } from '@/redux/store/slices/ui/uiSlice'
import { useAppSelector } from '@redux/hooks/useAppState'
import { useDebounce } from '@uidotdev/usehooks'
import { useEffect, useRef, useState } from 'react'

const COUNTRY_BOUND = 'us'

const searchEntry = (description: string) => ({
	description,
	matched_substrings: [],
	place_id: 'search',
	structured_formatting: {
		main_text: '',
		secondary_text: '',
		main_text_matched_substrings: [],
	},
	terms: [],
	types: [],
})

export const usePlaces = (withSearch = false) => {
	if (!window.google.maps.places) {
		throw new Error('Google places library not loaded')
	}

	const hasSearched = useRef(false)
	const autocompleteService = useRef(new window.google.maps.places.AutocompleteService())
	const placesService = useRef(
		new window.google.maps.places.PlacesService(document.createElement('div'))
	)
	const [query, setQuery] = useState('')
	const debouncedQuery = useDebounce(query, 500)
	const [queryResults, setQueryResults] = useState<google.maps.places.PlaceResult[]>([])
	const [placePredictions, setPlacePredictions] = useState<
		google.maps.places.AutocompletePrediction[]
	>(withSearch ? [searchEntry(debouncedQuery)] : [])
	const viewBounds = useAppSelector(mapViewBounds)

	useEffect(() => {
		if (!debouncedQuery) return

		withSearch &&
			setPlacePredictions(([searchPrediction]) => [
				{
					...searchPrediction,
					description: debouncedQuery,
				},
			])

		autocompleteService.current.getPlacePredictions(
			{
				input: debouncedQuery,
				types: ['establishment'],
				componentRestrictions: {
					country: COUNTRY_BOUND,
				},
			},
			(
				predictions: google.maps.places.AutocompletePrediction[] | null,
				status: google.maps.places.PlacesServiceStatus
			) => {
				hasSearched.current = true
				if (status === google.maps.places.PlacesServiceStatus.OK && predictions !== null) {
					setPlacePredictions(([searchPrediction]) =>
						withSearch ? [searchPrediction, ...predictions] : predictions
					)
				}
			}
		)
	}, [debouncedQuery, withSearch])

	const handleSearch = () => {
		withSearch &&
			placesService.current.textSearch(
				{
					query,
					region: COUNTRY_BOUND,
					...(viewBounds && {
						bounds: new google.maps.LatLngBounds(viewBounds?.southWest, viewBounds?.northEast),
					}),
				},
				(
					results: google.maps.places.PlaceResult[] | null,
					status: google.maps.places.PlacesServiceStatus
				) => {
					if (status === google.maps.places.PlacesServiceStatus.OK) {
						const locations = (results ?? []).filter((r) => !!r.place_id)
						setQueryResults(locations)
					}
				}
			)
	}

	const clearSearchResults = () => setQueryResults([])
	const clearPlacePredictions = () => setPlacePredictions(withSearch ? [searchEntry('')] : [])

	return {
		hasSearched,
		query,
		setQuery,
		placePredictions,
		handleSearch,
		queryResults,
		clearSearchResults,
		clearPlacePredictions,
	}
}
